// Page Emploi Unifiée - Desktop
// Layout avec onglets horizontaux + vue master-detail (liste à gauche, détail à droite)

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, MapPin, Briefcase, Building2, FileText, Clock, Euro, Heart,
  Filter, ChevronRight, Zap, Laptop, Bookmark, BookmarkPlus, X, Bell,
  Home, SlidersHorizontal, Users, Send, Phone, Mail, ExternalLink,
  GraduationCap, Upload, Check, User, AlertTriangle
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de contrat
const JOB_TYPES = [
  { id: 'cdi', label: 'CDI', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'cdd', label: 'CDD', color: 'bg-blue-100 text-blue-700' },
  { id: 'interim', label: 'Intérim', color: 'bg-orange-100 text-orange-700' },
  { id: 'apprentissage', label: 'Apprentissage', color: 'bg-purple-100 text-purple-700' },
];

// Mock data
const MOCK_JOBS = [
  { id: 'job-1', title: 'Électricien qualifié H/F', company: 'BTP Solutions', city: 'Lyon', job_type: 'cdi', salary_min: 2200, salary_max: 2800, is_urgent: true, description: 'Nous recherchons un électricien qualifié pour rejoindre notre équipe dynamique...', created_at: new Date(Date.now() - 1 * 24 * 3600000).toISOString() },
  { id: 'job-2', title: 'Plombier chauffagiste', company: 'Thermik Pro', city: 'Marseille', job_type: 'cdi', salary_min: 2400, salary_max: 3200, is_urgent: false, description: 'Rejoignez une entreprise familiale spécialisée dans le chauffage...', created_at: new Date(Date.now() - 2 * 24 * 3600000).toISOString() },
  { id: 'job-3', title: 'Maçon coffreur bancheur', company: 'Constructions Modernes', city: 'Paris', job_type: 'cdi', salary_min: 2000, salary_max: 2600, is_urgent: true, description: 'Pour des chantiers de grande envergure, nous recrutons...', created_at: new Date(Date.now() - 3 * 24 * 3600000).toISOString() },
  { id: 'job-4', title: 'Carreleur expérimenté', company: 'Artisan Carrelage', city: 'Bordeaux', job_type: 'cdd', salary_min: 1900, salary_max: 2300, is_urgent: false, description: 'CDD de 6 mois pour un chantier de rénovation...', created_at: new Date(Date.now() - 5 * 24 * 3600000).toISOString() },
  { id: 'job-5', title: 'Chef de chantier', company: 'Constructions Modernes', city: 'Paris', job_type: 'cdi', salary_min: 3500, salary_max: 4200, is_urgent: true, description: 'Nous recherchons un chef de chantier expérimenté...', created_at: new Date(Date.now() - 4 * 24 * 3600000).toISOString() },
  { id: 'job-6', title: 'Technicien chauffagiste', company: 'Thermik Pro', city: 'Marseille', job_type: 'cdi', salary_min: 2200, salary_max: 2800, is_urgent: false, description: 'Installation et maintenance de systèmes de chauffage...', created_at: new Date(Date.now() - 6 * 24 * 3600000).toISOString() },
  { id: 'job-7', title: 'Électricien industriel', company: 'BTP Solutions', city: 'Lyon', job_type: 'cdi', salary_min: 2500, salary_max: 3000, is_urgent: false, description: 'Poste en milieu industriel...', created_at: new Date(Date.now() - 7 * 24 * 3600000).toISOString() },
  { id: 'job-8', title: 'Poseur de faïence', company: 'Artisan Carrelage', city: 'Bordeaux', job_type: 'cdi', salary_min: 2000, salary_max: 2500, is_urgent: true, description: 'Spécialiste faïence murale...', created_at: new Date(Date.now() - 2 * 24 * 3600000).toISOString() },
];

// Mock entreprises
const MOCK_COMPANIES = [
  { id: 1, name: 'BTP Solutions', location: 'Lyon', employees: '50-100', rating: 4.5, openJobs: 12, sectors: ['Électricité', 'Plomberie'] },
  { id: 2, name: 'Constructions Modernes', location: 'Paris', employees: '200-500', rating: 4.2, openJobs: 25, sectors: ['Gros œuvre', 'Charpente'] },
  { id: 3, name: 'Thermik Pro', location: 'Marseille', employees: '20-50', rating: 4.8, openJobs: 5, sectors: ['Chauffage', 'Climatisation'] },
  { id: 4, name: 'Artisan Carrelage', location: 'Bordeaux', employees: '10-20', rating: 4.6, openJobs: 3, sectors: ['Carrelage', 'Faïence'] },
];

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return "Hier";
  if (diff < 7) return `Il y a ${diff}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// Format salaire
const formatSalary = (job) => {
  if (!job.salary_min && !job.salary_max) return null;
  if (job.salary_min && job.salary_max) {
    return `${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()} €/mois`;
  }
  return `${(job.salary_min || job.salary_max).toLocaleString()} €/mois`;
};

export default function EmploiUnifiedPage({ onOpenFilters, defaultTab = 'offres' }) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [jobs, setJobs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedJobs, setSavedJobs] = useState(new Set());
  const [companyFilter, setCompanyFilter] = useState(null); // Filtre par entreprise

  // Onglets
  const tabs = [
    { id: 'offres', label: 'Toutes les offres BTP', icon: Home, color: 'orange' },
    { id: 'recherche', label: 'Trouve ton emploi en 3 clics', icon: SlidersHorizontal, color: 'blue' },
    { id: 'entreprises', label: 'Entreprises qui recrutent', icon: Building2, color: 'emerald' },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/jobs`);
      if (res.ok) {
        const data = await res.json();
        setJobs(data.jobs || MOCK_JOBS);
      } else {
        setJobs(MOCK_JOBS);
      }
      setCompanies(MOCK_COMPANIES);
    } catch (e) {
      console.error('Error:', e);
      setJobs(MOCK_JOBS);
      setCompanies(MOCK_COMPANIES);
    } finally {
      setLoading(false);
    }
  };

  const toggleSaveJob = (jobId) => {
    setSavedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
        toast.success('Offre retirée des favoris');
      } else {
        newSet.add(jobId);
        toast.success('Offre sauvegardée');
      }
      return newSet;
    });
  };

  const handleOpenFilters = () => {
    if (onOpenFilters) {
      onOpenFilters();
    }
    setActiveTab('recherche');
  };

  // Voir les offres d'une entreprise
  const handleViewCompanyJobs = (companyName) => {
    setCompanyFilter(companyName);
    setActiveTab('offres');
    toast.success(`Offres de ${companyName}`);
  };

  // Effacer le filtre entreprise
  const clearCompanyFilter = () => {
    setCompanyFilter(null);
  };

  // Jobs filtrés
  const filteredJobs = companyFilter 
    ? jobs.filter(job => job.company === companyFilter)
    : jobs;

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Sur mobile, afficher soit la liste soit le détail
  const [mobileView, setMobileView] = useState('list'); // 'list' ou 'detail'

  // Quand on sélectionne un job sur mobile, passer en vue détail
  const handleSelectJob = (job) => {
    setSelectedJob(job);
    // Sur mobile (< 640px), passer en vue détail
    if (window.innerWidth < 640) {
      setMobileView('detail');
    }
  };

  // Retour à la liste sur mobile
  const handleBackToList = () => {
    setMobileView('list');
    setSelectedJob(null);
  };
  
  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* ═══ CONTENU ═══ */}
      <div className="flex-1 overflow-hidden">
        
        {/* ─── TAB: TOUTES LES OFFRES BTP ─── */}
        {activeTab === 'offres' && (
          <div className="h-full flex flex-col sm:flex-row">
            {/* Liste des offres - Mobile: pleine largeur, Desktop: moitié */}
            <div className={`
              ${mobileView === 'detail' ? 'hidden sm:flex' : 'flex'}
              sm:w-1/2 w-full border-r border-gray-200 flex-col bg-white flex-1 sm:flex-none
            `}>
              {/* Header liste */}
              <div className="px-3 py-2.5 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">
                    <span className="font-semibold text-gray-900">{filteredJobs.length}</span> offres d'emploi
                  </span>
                  <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={handleOpenFilters}>
                    <Filter size={12} className="mr-1" /> Filtrer
                  </Button>
                </div>
                {/* Filtre entreprise actif */}
                {companyFilter && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <span className="text-[10px] text-gray-500">Filtre :</span>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-medium rounded-full">
                      <Building2 size={10} />
                      {companyFilter}
                      <button onClick={clearCompanyFilter} className="ml-0.5 hover:text-emerald-900">
                        <X size={10} />
                      </button>
                    </span>
                  </div>
                )}
              </div>
              
              {/* Liste scrollable */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full" />
                  </div>
                ) : filteredJobs.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {filteredJobs.map(job => {
                      const isSelected = selectedJob?.id === job.id;
                      const isSaved = savedJobs.has(job.id);
                      const jobType = JOB_TYPES.find(t => t.id === job.job_type) || JOB_TYPES[0];
                      
                      return (
                        <div
                          key={job.id}
                          onClick={() => handleSelectJob(job)}
                          className={`p-3 sm:p-4 cursor-pointer transition-colors ${
                            isSelected ? 'bg-orange-50 border-l-4 border-orange-500' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              {/* Badges */}
                              <div className="flex items-center gap-2 mb-1">
                                {job.is_urgent && (
                                  <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded flex items-center gap-0.5">
                                    <Zap size={8} /> URGENT
                                  </span>
                                )}
                                <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${jobType.color}`}>
                                  {jobType.label}
                                </span>
                              </div>
                              
                              {/* Titre - ne pas tronquer sur mobile */}
                              <h3 className="text-sm font-semibold text-gray-900 mb-0.5">{job.title}</h3>
                              
                              {/* Entreprise */}
                              <p className="text-xs text-gray-600 font-medium mb-1">{job.company}</p>
                              
                              {/* Infos */}
                              <div className="flex items-center gap-2 text-[11px] text-gray-500 flex-wrap">
                                <span className="flex items-center gap-0.5">
                                  <MapPin size={10} /> {job.city}
                                </span>
                                {formatSalary(job) && (
                                  <span className="flex items-center gap-0.5">
                                    <Euro size={10} /> {formatSalary(job)}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex flex-col items-center gap-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleSaveJob(job.id); }}
                                className={`p-1.5 rounded-lg transition-colors ${
                                  isSaved ? 'bg-orange-100 text-orange-600' : 'hover:bg-gray-100 text-gray-400'
                                }`}
                              >
                                {isSaved ? <Bookmark size={14} fill="currentColor" /> : <BookmarkPlus size={14} />}
                              </button>
                              {/* Flèche sur mobile pour indiquer qu'on peut cliquer */}
                              <ChevronRight size={14} className="text-gray-300 sm:hidden" />
                            </div>
                          </div>
                          
                          {/* Date */}
                          <p className="text-[10px] text-gray-400 mt-1.5">{formatDate(job.created_at)}</p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-center px-4">
                    <Briefcase size={48} className="text-gray-300 mb-4" />
                    <h3 className="font-semibold text-gray-700 mb-1">
                      {companyFilter ? `Aucune offre pour ${companyFilter}` : 'Aucune offre trouvée'}
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      {companyFilter ? 'Cette entreprise n\'a pas d\'offre actuellement' : 'Modifie tes critères de recherche ou crée une alerte'}
                    </p>
                    <div className="flex gap-2">
                      {companyFilter ? (
                        <Button variant="outline" size="sm" onClick={clearCompanyFilter}>
                          Voir toutes les offres
                        </Button>
                      ) : (
                        <>
                          <Button variant="outline" size="sm" onClick={() => fetchData()}>
                            Voir toutes les offres
                          </Button>
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                            <Bell size={14} className="mr-1" /> Créer une alerte
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Détail de l'offre - Mobile: pleine largeur (si sélectionné), Desktop: moitié */}
            <div className={`
              ${mobileView === 'list' ? 'hidden sm:flex' : 'flex'}
              sm:w-1/2 w-full flex-col bg-white flex-1 sm:flex-none
            `}>
              {selectedJob ? (
                <div className="flex-1 overflow-y-auto">
                  {/* Bouton retour sur mobile */}
                  <button 
                    onClick={handleBackToList}
                    className="sm:hidden flex items-center gap-2 px-4 py-3 text-sm text-gray-600 border-b border-gray-100 w-full text-left hover:bg-gray-50"
                  >
                    <ChevronRight size={16} className="rotate-180" />
                    Retour aux offres
                  </button>
                  
                  {/* Header offre */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        {selectedJob.is_urgent && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded mb-1">
                            <Zap size={9} /> URGENT
                          </span>
                        )}
                        <h2 className="text-base sm:text-sm font-semibold text-gray-900">{selectedJob.title}</h2>
                        <p className="text-sm sm:text-xs text-orange-600 font-medium mt-0.5">{selectedJob.company}</p>
                      </div>
                      <button
                        onClick={() => toggleSaveJob(selectedJob.id)}
                        className={`p-2 sm:p-1.5 rounded-lg transition-colors ${
                          savedJobs.has(selectedJob.id) ? 'bg-orange-100 text-orange-600' : 'hover:bg-gray-100 text-gray-400'
                        }`}
                      >
                        {savedJobs.has(selectedJob.id) ? <Bookmark size={20} fill="currentColor" /> : <BookmarkPlus size={20} />}
                      </button>
                    </div>
                    
                    {/* Infos clés */}
                    <div className="flex flex-wrap gap-3 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <MapPin size={12} className="text-gray-400" /> {selectedJob.city}
                      </span>
                      <span className="flex items-center gap-1">
                        <Briefcase size={12} className="text-gray-400" /> {JOB_TYPES.find(t => t.id === selectedJob.job_type)?.label || 'CDI'}
                      </span>
                      {formatSalary(selectedJob) && (
                        <span className="flex items-center gap-1">
                          <Euro size={12} className="text-gray-400" /> {formatSalary(selectedJob)}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock size={12} className="text-gray-400" /> {formatDate(selectedJob.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Description */}
                  <div className="p-4 sm:p-5">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Description du poste</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {selectedJob.description || 'Aucune description disponible pour cette offre.'}
                    </p>
                  </div>
                  
                  {/* Actions */}
                  <div className="p-4 border-t border-gray-100 bg-gray-50">
                    <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white text-sm">
                      <Send size={14} className="mr-1.5" /> Postuler maintenant
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
                  <Briefcase size={48} className="text-gray-200 mb-3" />
                  <h3 className="text-sm font-medium text-gray-600 mb-0.5">Sélectionnez une offre</h3>
                  <p className="text-xs text-gray-400">Cliquez sur une offre pour voir les détails</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── TAB: RECHERCHE (placeholder - utilise les filtres) ─── */}
        {activeTab === 'recherche' && (
          <div className="h-full flex items-center justify-center bg-white">
            <div className="text-center px-8">
              <SlidersHorizontal size={64} className="mx-auto text-blue-200 mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Trouve ton emploi en 3 clics</h3>
              <p className="text-gray-500 mb-6">Utilise les filtres pour affiner ta recherche</p>
              <Button onClick={handleOpenFilters} className="bg-blue-600 hover:bg-blue-700">
                <Filter size={18} className="mr-2" /> Ouvrir les filtres
              </Button>
            </div>
          </div>
        )}

        {/* ─── TAB: ENTREPRISES QUI RECRUTENT ─── */}
        {activeTab === 'entreprises' && (
          <div className="h-full overflow-y-auto p-4">
            <div className="max-w-4xl mx-auto">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                  <p className="text-xl font-bold text-emerald-600">{companies.length}</p>
                  <p className="text-xs text-gray-500">Entreprises</p>
                </div>
                <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                  <p className="text-xl font-bold text-blue-600">{companies.reduce((sum, c) => sum + c.openJobs, 0)}</p>
                  <p className="text-xs text-gray-500">Offres actives</p>
                </div>
                <div className="bg-white rounded-xl p-3 text-center shadow-sm">
                  <p className="text-xl font-bold text-orange-600">15+</p>
                  <p className="text-xs text-gray-500">Secteurs</p>
                </div>
              </div>
              
              {/* Liste entreprises */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {companies.map(company => (
                  <div key={company.id} className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">{company.name}</h3>
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mt-0.5">
                          <MapPin size={10} />
                          <span>{company.location}</span>
                          <span>•</span>
                          <Users size={10} />
                          <span>{company.employees}</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {company.sectors.map((s, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] text-gray-600">
                              {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <span className="text-xs">
                        <span className="font-semibold text-emerald-600">{company.openJobs}</span>
                        <span className="text-gray-500"> offres</span>
                      </span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="h-7 text-xs px-2"
                        onClick={() => handleViewCompanyJobs(company.name)}
                      >
                        Voir les offres <ExternalLink size={12} className="ml-1" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
